import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/base/SEOSchema';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const [sortBy, setSortBy] = useState<'latest' | 'hot'>('latest');

  const categoryArticles = useMemo(() => {
    const filtered = articles.filter((a) => a.category === slug);
    if (sortBy === 'latest') {
      return [...filtered].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    }
    return [...filtered].sort((a, b) => b.readCount - a.readCount);
  }, [slug, sortBy]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-heading font-semibold text-foreground-950">分类未找到</h2>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryNameForSEO = currentCategory.name;

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="organization" />
      <SEOSchema
        type="webpage"
        data={{
          name: `${categoryNameForSEO} - ${siteInfo.name}`,
          description: currentCategory.description,
          url: `${siteInfo.url}/category/${slug}`,
        }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 pt-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {currentCategory.name}
                </h1>
                <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-xl">
                  {currentCategory.description}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">共 {categoryArticles.length} 篇</span>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <span className="text-sm text-foreground-600 whitespace-nowrap">排序：</span>
              <button
                onClick={() => setSortBy('latest')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  sortBy === 'latest'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                最新发布
              </button>
              <button
                onClick={() => setSortBy('hot')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  sortBy === 'hot'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                最受欢迎
              </button>
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-base w-4 flex items-center justify-center`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    {...article}
                    categoryName={currentCategory.name}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-file-search-line text-3xl text-foreground-400"></i>
                </div>
                <p className="mt-4 text-foreground-600">该分类下暂无作品</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}